import {
  FUEL_LOW,
  OBSTRUCTION_MEANS,
  OBSTRUCTION_WORDS,
  type Obstruction,
} from '@hotlist/shared';

/**
 * The things you can do right now, as buttons.
 *
 * Every action in this game was a keyboard shortcut, which meant the phone
 * layout was a viewer: you could pan a map and read panels and do **nothing**.
 * Making the panels responsive without giving anybody a way to act was building
 * half an interface and calling it done.
 *
 * So this is the other half, and it is not a phone concession. A row of buttons
 * that says what is possible *right now* is better than nine memorised keys on
 * every device, because the interesting actions in this game are contextual --
 * you cannot cover a camera you are not standing next to, and there is no point
 * offering it. The keys still work; nothing was taken away.
 *
 * ## The rule this follows
 *
 * **A button appears when the action is possible and not before.** Not greyed
 * out, not there-but-refusing: absent. A disabled "cover camera" button tells a
 * player there is a camera somewhere near them, which is coverage they have not
 * earned by driving, and the whole civilian side of this game is built on
 * earning that. The same reasoning already keeps enforcement tabs off a
 * civilian's console.
 */

export type ActionName =
  | 'handin'
  | 'jobdrive'
  | 'fuel'
  | 'stop'
  | 'follow'
  | 'pursue'
  | 'breakoff'
  | 'sendunit'
  | 'pullover'
  | 'avoid'
  | 'obstruct'
  | `obstruct:${Obstruction}`
  | 'camera'
  | 'damage'
  | 'take'
  | 'share'
  | 'network'
  | 'run';

export interface ActionState {
  /** Moving, so there is something to stop. */
  driving: boolean;
  /** The camera is chasing the player's own car. */
  following: boolean;
  /** A car is selected and close enough to stop. */
  canPullOver: boolean;
  /** The plate of that car, for the button label. */
  selectedPlate: string;
  /** A car is selected at all -- enough to give chase, plate legible or not. */
  hasSelection: boolean;
  /** This officer is running a pursuit right now. */
  pursuing: boolean;
  /** The selected car's plate if it can be read, else empty -- for the chase label. */
  pursuePlate: string;
  /** Standing near a camera this player has found. */
  nearCamera: boolean;
  /** How close, so the heavier tiers can be offered only when they are possible. */
  cameraDistance: number;
  /** This player has found at least one camera. */
  hasMap: boolean;
  /** How many, because the button should say what it is avoiding. */
  mapSize: number;
  /** Whether other people's reports are currently drawn. */
  showingCommunity: boolean;
  /** Routing around what they found. */
  avoiding: boolean;
  /** Enforcement access. */
  enforcement: boolean;
  /** The shared network is on. */
  networked: boolean;
  /** The administrator's speed allowance is available. */
  isAdmin: boolean;
  /** ...and currently in use. */
  running: boolean;
  /** Carrying a job. */
  hasJob: boolean;
  /** Standing at its drop, so it can be handed in. */
  atDrop: boolean;
  /** What it pays, for the button. */
  jobPays: number;
  /** Where it goes, for the name on the drive button. */
  jobTo: string;
  /** Whether the leg in hand is the pickup rather than the delivery. */
  collecting: boolean;
  /**
   * Already driving to the job's target.
   *
   * Which is the normal state, and the reason the drive button is usually not
   * offered: taking a job in the work app already sets off toward it.
   */
  onJobRoute: boolean;
  /** How much is left in the tank, as a fraction. */
  fuelShare: number;
  /** The nearest filling station, named. */
  fuelStop: string;
  /** ...and whether the car is standing at it. */
  atPump: boolean;
}

interface Button {
  name: ActionName;
  label: (state: ActionState) => string;
  /** Whether this action is possible at all right now. */
  when: (state: ActionState) => boolean;
  /** Whether it reads as currently on. */
  active?: (state: ActionState) => boolean;
  /** Heavier than the rest, and styled so. */
  heavy?: boolean;
}

/**
 * Reach for the interference tiers, mirrored from INTERFERENCE_REACH.
 *
 * Duplicated deliberately and narrowly: the client is deciding what to *offer*,
 * and the server still decides what happens. If these drift the worst case is a
 * button that produces a polite refusal, which is a great deal better than
 * importing the server's reach constants into the renderer.
 */
const REACH = { obstruct: 30, damage: 20, take: 12 } as const;

/**
 * Trim a real business name to something a button can hold.
 *
 * "Village of Elm Grove Department of Public Works" is a real place with a real
 * name, and it does not fit on a control.
 */
function short(name: string, limit = 20): string {
  return name.length <= limit ? name : `${name.slice(0, limit - 1).trimEnd()}…`;
}

/**
 * Which side each of these belongs to.
 *
 * The bar was showing every action to everybody, so an officer on shift was
 * offered "avoid 2 found" and "see all (files your 2)" -- routing around their
 * own department's cameras, and filing crowdsourced sightings of them to a map
 * built by people trying to keep away from them. Both are civilian verbs and
 * one of them is close to the opposite of the job.
 *
 * The gate is `!s.enforcement` rather than a role list, because §7's point is
 * that these are two halves of one game and a player moves between them: the
 * bar has to change when they do, not when they log in.
 */
const BUTTONS: readonly Button[] = Object.freeze([
  {
    // First, because when you are standing at the place you drove to, it is the
    // only thing you came here to do.
    name: 'handin',
    label: (s) => (s.collecting ? 'collect it' : `deliver — $${s.jobPays}`),
    when: (s) => !s.enforcement && s.hasJob && s.atDrop,
  },
  {
    /**
     * Back onto the job, after something took you off it.
     *
     * **Only when you are not already going there.** This used to sit in the
     * bar for the whole length of every job, duplicating the button in the work
     * app that had just set you off -- so it read as a toggle for a thing that
     * was already happening, which is a control that cannot be understood.
     *
     * What it is actually for is recovery: you tapped the map by accident, or
     * went for fuel, and now the car is driving somewhere that is not the job.
     * That is a real situation and it needs a way out, so the button appears
     * exactly then and says what it is.
     */
    name: 'jobdrive',
    label: (s) => `back to ${short(s.jobTo)}`,
    when: (s) => !s.enforcement && s.hasJob && !s.atDrop && s.jobTo !== '' && !s.onJobRoute,
  },
  {
    // Offered at the pump, and offered as a way *to* a pump once the tank is
    // low enough to be worth doing something about. Not before: a fuel button
    // on screen at all times is a fuel button nobody reads.
    name: 'fuel',
    label: (s) =>
      s.atPump ? `fill up at ${short(s.fuelStop)}` : `low fuel — ${short(s.fuelStop)}`,
    when: (s) => s.fuelStop !== '' && (s.atPump || s.fuelShare <= FUEL_LOW),
    active: (s) => s.atPump,
  },
  {
    name: 'stop',
    label: () => 'stop',
    when: (s) => s.driving,
  },
  {
    name: 'follow',
    label: (s) => (s.following ? 'stop following' : 'follow me'),
    when: () => true,
    active: (s) => s.following,
  },
  {
    name: 'pullover',
    label: (s) => `pull over ${s.selectedPlate}`,
    when: (s) => s.enforcement && s.canPullOver,
    heavy: true,
  },
  {
    /**
     * Light somebody up.
     *
     * Offered on any car you have selected, not only one whose plate you can
     * read: you decide to chase on the make and the driving from down the block,
     * and the plate is what you confirm once the cruiser has closed on it. The
     * server has the final say on whether you actually had a line to it. Hidden
     * once the chase is on, because then the control you want is "break off",
     * not "start another".
     */
    name: 'pursue',
    label: (s) => (s.pursuePlate ? `pursue ${s.pursuePlate}` : 'pursue this car'),
    when: (s) => s.enforcement && s.hasSelection && !s.pursuing,
    heavy: true,
  },
  {
    name: 'breakoff',
    label: () => 'break off',
    when: (s) => s.pursuing,
    active: () => true,
  },
  {
    /**
     * Send an NPC unit after the selected car instead of chasing it yourself.
     * Offered when you can read the plate (that is the intel), and not while you
     * are already running your own pursuit.
     */
    name: 'sendunit',
    label: (s) => `send a unit after ${s.selectedPlate}`,
    when: (s) => s.enforcement && s.canPullOver && !s.pursuing,
  },
  {
    name: 'camera',
    label: () => 'camera here',
    when: (s) => !s.enforcement && s.nearCamera && s.cameraDistance <= REACH.obstruct,
  },
  {
    name: 'network',
    label: (s) => (s.networked ? 'network on' : 'network off'),
    when: (s) => s.enforcement,
    active: (s) => s.networked,
  },
  {
    /**
     * Foot down, for everybody.
     *
     * It was labeled "run", which reads as fleeing on foot, and it was shown
     * only to the administrator. Both were wrong. It is not a verb, it is the
     * accelerator: it doubles the limit, and it is the one ordinary law the
     * civilian side can choose to break.
     *
     * Two consequences, deliberately not alike. Every camera you pass records
     * the speed and keeps it; any marked unit that happens to be within five
     * hundred feet pulls you over there and then. Same offence. One of them
     * needed somebody to be present and one of them did not, and that is the
     * whole argument of the game sitting behind one button.
     *
     * On the enforcement side it also brings the lights.
     */
    name: 'run',
    label: (s) => (s.running ? '2× speed on' : '2× speed'),
    when: () => true,
    active: (s) => s.running,
  },
]);

export class ActionBar {
  private readonly root: HTMLElement;
  private readonly buttons = new Map<ActionName, HTMLButtonElement>();
  /** What was on screen last frame, so the DOM is only touched on a change. */
  private shown = '';

  constructor(onAction: (name: ActionName) => void) {
    this.root = must('actions');
    for (const spec of BUTTONS) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = spec.heavy ? 'action heavy' : 'action';
      button.dataset.action = spec.name;
      button.hidden = true;
      button.addEventListener('click', () => onAction(spec.name));
      this.root.append(button);
      this.buttons.set(spec.name, button);
    }
  }

  update(state: ActionState): void {
    // Cheap identity of the whole bar. Rebuilding labels every frame at 60 Hz
    // would be a layout pass per frame for a row of buttons that changes maybe
    // twice a minute.
    const key = BUTTONS.map((spec) =>
      spec.when(state) ? `${spec.name}:${spec.label(state)}:${spec.active?.(state) ? 1 : 0}` : '',
    ).join('|');
    if (key === this.shown) return;
    this.shown = key;

    let any = false;
    for (const spec of BUTTONS) {
      const button = this.buttons.get(spec.name);
      if (!button) continue;
      const possible = spec.when(state);
      button.hidden = !possible;
      if (!possible) continue;
      any = true;
      button.textContent = spec.label(state);
      button.classList.toggle('on', spec.active?.(state) === true);
    }
    this.root.hidden = !any;
  }
}

function must(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`missing element #${id}`);
  return element;
}
